/**
 * Password page behaviours:
 *  - <em-marquee>: endless horizontal text loop (speed relative to the element width)
 *  - store access <dialog> opened from [data-em-password-open]
 */

class EmMarquee extends HTMLElement {
  connectedCallback() {
    this.source = this.source || this.firstElementChild;
    if (!this.source) return;

    this.resizeObserver = new ResizeObserver(() => this.build());
    this.resizeObserver.observe(this);
    this.build();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.animation?.cancel();
  }

  build() {
    const width = this.clientWidth;
    if (!width || !this.source) return;
    if (width === this.builtWidth) return;
    this.builtWidth = width;

    this.animation?.cancel();

    const track = document.createElement('div');
    track.className = 'em-password__marquee-track';
    track.appendChild(this.source.cloneNode(true));
    this.replaceChildren(track);

    const itemWidth = track.firstElementChild.getBoundingClientRect().width || 1;
    const copies = Math.ceil(width / itemWidth) + 1;

    for (let index = 0; index < copies; index += 1) {
      const clone = this.source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const forward = this.dataset.direction !== 'backward';
    const speed = parseFloat(this.dataset.speed) || 0.1;
    const durationSeconds = (1 / speed) * (width / 300);
    const from = forward ? -itemWidth : 0;
    const to = forward ? 0 : -itemWidth;

    this.animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationSeconds * 1000, iterations: Infinity, easing: 'linear' }
    );
  }
}

if (!customElements.get('em-marquee')) {
  customElements.define('em-marquee', EmMarquee);
}

function setupPasswordDialog(root = document) {
  root.querySelectorAll('[data-em-password-open]').forEach((trigger) => {
    if (trigger.dataset.emBound) return;
    trigger.dataset.emBound = 'true';

    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!(dialog instanceof HTMLDialogElement)) return;

    trigger.addEventListener('click', () => dialog.showModal());

    dialog.querySelector('[data-em-password-close]')?.addEventListener('click', () => dialog.close());

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', () => trigger.focus());

    if (dialog.hasAttribute('data-open-on-load') && !dialog.open) {
      dialog.showModal();
    }
  });
}

setupPasswordDialog();

document.addEventListener('shopify:section:load', (event) => setupPasswordDialog(event.target));
